import { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Rnd } from 'react-rnd';
import {
  Card,
  CardContent,
  Typography,
  CardActions,
  IconButton,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Menu,
  MenuItem,
  CardMedia,
  Tooltip,
  CircularProgress,
  Box,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import CloseIcon from '@mui/icons-material/Close';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import PendingIcon from '@mui/icons-material/Pending';
import RouteIcon from '@mui/icons-material/Route';
import SendIcon from '@mui/icons-material/Send';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AnchorIcon from '@mui/icons-material/Anchor';
import CameraAltIcon from '@mui/icons-material/CameraAlt';

import { useTranslation } from './LocalizationProvider';
import RemoveDialog from './RemoveDialog';
import PositionValue from './PositionValue';
import { useDeviceReadonly, useAdministrator, useManager, useLimitCommand, useRestriction } from '../util/permissions';
import usePositionAttributes from '../attributes/usePositionAttributes';
import { devicesActions, errorsActions } from '../../store';
import { useCatch, useCatchCallback } from '../../reactHelper';
import { useAttributePreference } from '../util/preferences';
import fetchOrThrow from '../util/fetchOrThrow';
import { startStreaming } from '../util/cameras';
import { getStatusColor } from '../../common/util/formatter';
import Hls from 'hls.js';

// Custom Dialogs
import MultiActionDialog from './MultiActionDialog';
import AnchorDialog from './AnchorDialog';
import RemoveAnchorDialog from './RemoveAnchorDialog';
import LockUnlockConfirmationDialog from './confirmation-dialog';
import { SuccessToast } from './success-toast';

// Audio functions
const playBlockSound = () => {
  try {
    const audio = new Audio('./resources/bloqueio.wav');
    audio.volume = 0.5;
    audio.play().catch(() => {
      const fallbackAudio = new Audio('./src/resources/bloqueio.wav');
      fallbackAudio.volume = 0.5;
      fallbackAudio.play().catch(console.warn);
    });
  } catch (error) {
    console.warn('Erro no áudio de bloqueio:', error);
  }
};

const playUnblockSound = () => {
  try {
    const audio = new Audio('./resources/desbloqueio.wav');
    audio.volume = 0.5;
    audio.play().catch(() => {
      const fallbackAudio = new Audio('./src/resources/desbloqueio.wav');
      fallbackAudio.volume = 0.5;
      fallbackAudio.play().catch(console.warn);
    });
  } catch (error) {
    console.warn('Erro no áudio de desbloqueio:', error);
  }
};

const hls = new Hls();
let count = 0;

const useStyles = makeStyles()((theme, { desktopPadding }) => ({
  card: {
    pointerEvents: 'auto',
    width: theme.dimensions.popupMaxWidth,
  },
  media: {
    height: theme.dimensions.popupImageHeight,
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'flex-start',
    position: 'relative',
    cursor: 'pointer', // ✅ CORREÇÃO: Cursor pointer para indicar clicável
  },
  mediaButton: {
    color: theme.palette.primary.contrastText,
    mixBlendMode: 'difference',
  },
  dragHandle: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '40px',
    zIndex: 10,
    cursor: 'move',
  },
  imageHeader: {
    textShadow: '1px 1px 2px black',
    position: 'absolute',
    top: '10px',
    left: '10px',
    zIndex: 2,
    pointerEvents: 'none', // ✅ CORREÇÃO: none para não interferir no clique do CardMedia
  },
  imageCloseButton: {
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
    zIndex: 11,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
    },
  },
  videoOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    zIndex: 5,
    backgroundColor: '#000',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(1, 1, 0, 2),
  },
  content: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    maxHeight: theme.dimensions.cardContentMaxHeight,
    overflow: 'auto',
  },
  icon: {
    width: '25px',
    height: '25px',
    filter: 'brightness(0) invert(1)',
  },
  table: {
    '& .MuiTableCell-sizeSmall': {
      paddingLeft: 0,
      paddingRight: 0,
    },
    '& .MuiTableCell-sizeSmall:first-of-type': {
      paddingRight: theme.spacing(1),
    },
  },
  cell: {
    borderBottom: 'none',
  },
  actions: {
    justifyContent: 'space-between',
  },
  root: {
    pointerEvents: 'none',
    position: 'fixed',
    zIndex: 5,
    left: '50%',
    [theme.breakpoints.up('md')]: {
      left: `calc(50% + ${desktopPadding} / 2)`,
      bottom: theme.spacing(3),
    },
    [theme.breakpoints.down('md')]: {
      left: '50%',
      bottom: `calc(${theme.spacing(3)} + ${theme.dimensions.bottomBarHeight}px)`,
    },
    transform: 'translateX(-50%)',
  },
}));

const StatusRow = ({ name, content }) => {
  const { classes } = useStyles({ desktopPadding: 0 });

  return (
    <TableRow>
      <TableCell className={classes.cell}>
        <Typography variant="body2">{name}</Typography>
      </TableCell>
      <TableCell className={classes.cell}>
        <Typography variant="body2" color="textSecondary">{content}</Typography>
      </TableCell>
    </TableRow>
  );
};

const StatusCard = ({ deviceId, position, onClose, disableActions, desktopPadding = 0 }) => {
  const { classes } = useStyles({ desktopPadding });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const t = useTranslation();

  const readonly = useRestriction('readonly');
  const deviceReadonly = useDeviceReadonly();
  const isAdmin = useAdministrator();
  const isManager = useManager();
  const limitCommands = useLimitCommand();

  const groups = useSelector((state) => state.groups.items);
  const shareDisabled = useSelector((state) => state.session.server.attributes.disableShare);
  const user = useSelector((state) => state.session.user);
  const revenda = useSelector((state) => state.session.revenda);
  const device = useSelector((state) => state.devices.items[deviceId]);

  const deviceImage = device?.attributes?.deviceImage;
  const statusKey = getStatusColor(device?.status);
  
  // ✅ CORREÇÃO 1: Usar hasAnchor ao invés de anchor
  const hasAnchor = Boolean(
    device?.attributes?.hasAnchor && 
    device.attributes.hasAnchor !== '' && 
    device.attributes.hasAnchor !== 'null' && 
    device.attributes.hasAnchor !== null
  );
  
  const hasValidGroup = device?.groupId && groups[device.groupId];

  const positionAttributes = usePositionAttributes(t);
  const positionItems = useAttributePreference('positionItems', 'fixTime,address,speed,totalDistance');

  const navigationAppLink = useAttributePreference('navigationAppLink');
  const navigationAppTitle = useAttributePreference('navigationAppTitle');

  const [anchorEl, setAnchorEl] = useState(null);
  const [streetView, setStreetView] = useState(false);

  // Video streaming states
  const [showVideo, setShowVideo] = useState(false);
  const [channel, setChannel] = useState(0);
  const [src, setSrc] = useState('');
  const [loadingVideo, setLoadingVideo] = useState(false);
  const [retry, setRetry] = useState(0);

  // Lock/Unlock states
  const [commandLoading, setCommandLoading] = useState(false);
  const [openLockUnlockDialog, setOpenLockUnlockDialog] = useState(false);
  const [sended, setSended] = useState(null);

  // Helper function to get blocked state
  const getBlockedState = useCallback(() => {
    if (!position?.attributes) return false;

    const blockedValue = position.attributes.blocked;

    if (typeof blockedValue === 'boolean') {
      return blockedValue;
    }
    if (typeof blockedValue === 'string') {
      return blockedValue.toLowerCase() === 'true' || blockedValue === '1';
    }
    if (typeof blockedValue === 'number') {
      return blockedValue === 1;
    }

    return false;
  }, [position]);

  const isBlocked = getBlockedState();

  // Dialog states
  const [openRemoveDialog, setOpenRemoveDialog] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState('delete');
  const [openAnchorDialog, setOpenAnchorDialog] = useState(false);
  const [openRemoveAnchorDialog, setOpenRemoveAnchorDialog] = useState(false);

  // Toast states
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastSeverity, setToastSeverity] = useState('success');
  const [toastOpen, setToastOpen] = useState(false);

  // Active panel state
  const [activePanel, setActivePanel] = useState(0);

  const handleRemove = useCatch(async (removed) => {
    if (removed) {
      const response = await fetchOrThrow('/api/devices');
      dispatch(devicesActions.refresh(await response.json()));
    }
    setOpenRemoveDialog(false);
  });

  const handleGeofence = useCatchCallback(async () => {
    const newItem = {
      name: t('sharedGeofence'),
      area: `CIRCLE (${position.latitude} ${position.longitude}, 50)`,
    };
    const response = await fetchOrThrow('/api/geofences', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(newItem),
    });
    const item = await response.json();
    await fetchOrThrow('/api/permissions', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ deviceId: position.deviceId, geofenceId: item.id }),
    });
    navigate(`/settings/geofence/${item.id}`);
  }, [navigate, position, t]);

  const handleCameraClick = useCallback(async () => {
    if (!showVideo && device) {
      setLoadingVideo(true);
      try {
        const _channel = device.attributes?.devicePassword ? count++ % 2 : count++ % 2 + 1;
        setChannel(_channel);
        const resp = await startStreaming(device.uniqueId, device.attributes?.devicePassword, _channel).then((r) => r.json());

        if (resp.data && /fail|error/i.test(resp.data._content)) {
          // ✅ CORREÇÃO 5: Toast aparece com mensagem de erro
          setToastMessage(resp.data._content);
          setToastSeverity('error');
          setShowSuccessToast(true);
          setLoadingVideo(false);
          return;
        }
        if (resp.data && resp.data._code !== '100') {
          // ✅ CORREÇÃO 5: Toast aparece com mensagem de erro
          setToastMessage(resp.data._msg);
          setToastSeverity('error');
          setShowSuccessToast(true);
          setLoadingVideo(false);
          return;
        }

        setTimeout(() => {
          setShowVideo(true);
          setLoadingVideo(false);
        }, 3000);
      } catch (error) {
        // ✅ CORREÇÃO 5: Toast aparece quando exceção ocorre
        setToastMessage(error.message || 'Erro ao iniciar câmera');
        setToastSeverity('error');
        setShowSuccessToast(true);
        setLoadingVideo(false);
      }
    } else {
      setShowVideo(false);
    }
  }, [showVideo, device]);

  const handleLockUnlockClick = useCallback(() => {
    if (commandLoading) return;
    setOpenLockUnlockDialog(true);
  }, [commandLoading]);

  const executeLockUnlock = useCallback(async () => {
    if (commandLoading) return;

    setCommandLoading(true);

    const currentBlockedState = getBlockedState();
    const commandType = currentBlockedState ? 'engineResume' : 'engineStop';

    if (!position?.deviceId && !deviceId) {
      setToastMessage('Erro: ID do dispositivo não encontrado');
      setToastSeverity('error');
      setToastOpen(true);
      setShowSuccessToast(true);
      setCommandLoading(false);
      return;
    }

    try {
      const requestBody = {
        deviceId: position?.deviceId || deviceId,
        type: commandType,
      };

      const response = await fetch('/api/commands/send', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
        },
        body: JSON.stringify(requestBody),
      });

      if (response.ok) {
        setSended(response.status);

        if (commandType === 'engineStop') {
          setToastMessage(t('deviceBlocked') || 'Comando de bloqueio enviado com sucesso!');
          playBlockSound();
        } else {
          setToastMessage(t('deviceUnblocked') || 'Comando de desbloqueio enviado com sucesso!');
          playUnblockSound();
        }

        setToastSeverity('success');
        setToastOpen(true);
        setShowSuccessToast(true);

        // Refresh devices after command sent
        const refreshAttempts = [1000, 3000, 5000];
        refreshAttempts.forEach((delay) => {
          setTimeout(async () => {
            try {
              const refreshResponse = await fetch('/api/devices');
              if (refreshResponse.ok) {
                const devices = await refreshResponse.json();
                dispatch(devicesActions.refresh(devices));
              }
            } catch (error) {
              console.error('Error refreshing devices:', error);
            }
          }, delay);
        });
      } else {
        throw new Error('Falha ao enviar comando');
      }
    } catch (error) {
      console.error('Erro ao executar bloqueio/desbloqueio:', error);
      setToastMessage(error.message || 'Erro ao executar comando');
      setToastSeverity('error');
      setShowSuccessToast(true);
      dispatch(errorsActions.push(error.message || 'Erro ao executar comando'));
    } finally {
      setCommandLoading(false);
      setOpenLockUnlockDialog(false);
    }
  }, [commandLoading, getBlockedState, position, deviceId, t, dispatch]);

  const handleDialogResult = useCatch(async (confirmed) => {
    setDialogOpen(false);
    if (confirmed) {
      try {
        const response = await fetch('/api/devices');
        const devices = await response.json();
        dispatch(devicesActions.refresh(devices));

        if (dialogType === 'delete' && onClose) {
          onClose();
        }
      } catch (err) {
        setToastMessage('Erro ao executar ação');
        setToastSeverity('error');
        setShowSuccessToast(true);
      }
    }
  });

  // Handler para clique no CardMedia (Street View)
  const handleStreetViewClick = useCallback(() => {
    if (position) {
      window.open(
        `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${position.latitude}%2C${position.longitude}&heading=${position.course || 0}`,
        '_blank'
      );
    }
  }, [position]);

  // Street View validation effect
  useEffect(() => {
    if (position) {
      fetch(
        `https://street-view.entrack-plataforma.workers.dev/?heading=${position.course || 0}&location=${position.latitude},${position.longitude}&size=288x144&return_error_code=true`,
      ).then((response) => setStreetView(response.ok));
    }
  }, [position]);

  // Mobile overflow effect
  useEffect(() => {
    if (isMobile) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [isMobile]);

  // Video stream URL effect
  useEffect(() => {
    if (device && showVideo) {
      const prefix = device.attributes?.devicePassword ? 'live/' : '';
      setSrc(`https://jimi-iothub-sec.fleetmap.io/${prefix}${channel}/${device.uniqueId}/hls.m3u8?retry=${retry}`);
    }
  }, [device, channel, retry, showVideo]);

  // HLS error handler effect
  useEffect(() => {
    const handleHlsError = () => {
      setRetry((prev) => prev + 1);
    };
    hls.on(Hls.Events.ERROR, handleHlsError);
    return () => {
      hls.off(Hls.Events.ERROR, handleHlsError);
    };
  }, []);

  if (!device) {
    return null;
  }

  return (
    <>
      <div className={classes.root}>
        <Rnd
          default={{ x: 0, y: 0, width: 'auto', height: 'auto' }}
          enableResizing={false}
          dragHandleClassName="draggable-header"
          style={{ position: 'relative' }}
          bounds={false}
          cancel=".no-drag"
        >
          <Card elevation={3} className={classes.card}>
            {!showVideo && (deviceImage || (position && streetView) ? (
              <CardMedia
                image={deviceImage ? `/api/media/${device.uniqueId}/${deviceImage}` : `https://street-view.entrack-plataforma.workers.dev/?heading=${position?.course || 0}&location=${position?.latitude},${position?.longitude}&size=288x144&return_error_code=true`}
                classes={{ root: classes.media }}
                // ✅ CORREÇÃO 3: onClick no CardMedia para Street View
                onClick={handleStreetViewClick}
              >
                {/* Região transparente para arrastar - topo do card */}
                <div 
                  className="draggable-header" 
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '40px',
                    zIndex: 10,
                    cursor: 'move',
                  }}
                  onClick={(e) => e.stopPropagation()} // Impede clique de propagar para CardMedia
                />
                
                {/* Botão de fechar */}
                <IconButton
                  className={classes.imageCloseButton}
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation(); // Impede clique de propagar para CardMedia
                    onClose();
                  }}
                  onTouchStart={(e) => {
                    e.stopPropagation();
                    onClose();
                  }}
                >
                  <CloseIcon fontSize="small" sx={{ color: 'white' }} />
                </IconButton>

                {/* Header com nome e grupo */}
                <div className={classes.imageHeader}>
                  <Typography variant="body1" sx={{ color: 'white', textShadow: '1px 1px 2px black' }}>
                    <b>{device.name.toUpperCase()}</b>
                  </Typography>
                  {hasValidGroup && (
                    <Typography variant="body2" sx={{ color: 'white', textShadow: '1px 1px 2px black' }}>
                      {groups[device.groupId].name}
                    </Typography>
                  )}
                </div>
              </CardMedia>
            ) : (
              <div className={`${classes.header} draggable-header`}>
                <Typography variant="body2" color="textSecondary">
                  {device.name}
                </Typography>
                <IconButton size="small" onClick={onClose} onTouchStart={onClose}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </div>
            ))}

            {/* ✅ CORREÇÃO 4: Vídeo sobreposto com drag handle */}
            {showVideo && src && (deviceImage || (position && streetView)) && (
              <div className={classes.videoOverlay}>
                {/* Região de arrasto no vídeo - topo */}
                <div 
                  className="draggable-header" 
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '40px',
                    zIndex: 12,
                    cursor: 'move',
                  }}
                />
                
                {/* Botão de fechar vídeo */}
                <IconButton
                  className={`${classes.imageCloseButton} no-drag`}
                  size="small"
                  onClick={() => {
                    setShowVideo(false);
                    setLoadingVideo(false);
                    setRetry(0);
                  }}
                  sx={{ zIndex: 13 }}
                >
                  <CloseIcon fontSize="small" sx={{ color: 'white' }} />
                </IconButton>
                
                <video
                  src={src}
                  onError={(e) => {
                    console.error('Erro no vídeo:', e);
                    const isMac = /iPhone|iPad|iPod|Macintosh|Mac OS X/.test(navigator.userAgent);
                    
                    // Tenta HLS se não for Mac e ainda não tentou muitas vezes
                    if (!isMac && src && retry < 3) {
                      try {
                        hls.loadSource(src);
                        hls.attachMedia(e.target);
                      } catch (hlsError) {
                        console.error('Erro HLS:', hlsError);
                        setTimeout(() => setRetry(retry + 1), 1000);
                      }
                    } else if (retry < 3) {
                      // Mac ou retry normal
                      setTimeout(() => setRetry(retry + 1), 1000);
                    } else {
                      // Após 3 tentativas: Toast + Fecha vídeo + Volta para imagem
                      setToastMessage('Não foi possível carregar o vídeo. Tente novamente mais tarde.');
                      setToastSeverity('error');
                      setShowSuccessToast(true);
                      setShowVideo(false);
                      setLoadingVideo(false);
                      setRetry(0);
                    }
                  }}
                  autoPlay
                  controls
                  style={{ width: '100%', height: '100%', display: 'block' }}
                />
              </div>
            )}

            {position && (
              <CardContent className={classes.content}>
                <Table size="small" classes={{ root: classes.table }}>
                  <TableBody>
                    {positionItems.split(',').filter((key) => position.hasOwnProperty(key) || position.attributes?.hasOwnProperty(key)).map((key) => (
                      <StatusRow
                        key={key}
                        name={positionAttributes[key]?.name || key}
                        content={(
                          <PositionValue
                            position={position}
                            property={position.hasOwnProperty(key) ? key : null}
                            attribute={position.hasOwnProperty(key) ? null : key}
                          />
                        )}
                      />
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            )}
            <CardActions classes={{ root: classes.actions }} disableSpacing>
              <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-around' }}>
                <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center' }}>
                  <Tooltip title={t('sharedExtra')}>
                    <span>
                      <IconButton
                        color="secondary"
                        onClick={(e) => setAnchorEl(e.currentTarget)}
                        disabled={!position}
                      >
                        <PendingIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
                {!disableActions && position && (
                  <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center' }}>
                    <Tooltip title={t('reportReplay')}>
                      <span>
                        <IconButton onClick={() => navigate(`/replay?deviceId=${deviceId}`)} disabled={!position}>
                          <RouteIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                )}
                <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center' }}>
                  <Tooltip title={t('camera') || 'Câmera'}>
                    <span>
                      <IconButton onClick={handleCameraClick} disabled={loadingVideo}>
                        {loadingVideo ? <CircularProgress size={20} /> : <CameraAltIcon />}
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
                {(isAdmin || isManager) && (
                  <>
                    <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center' }}>
                      <Tooltip title={t('commandTitle')}>
                        <span>
                          <IconButton onClick={() => navigate(`/settings/device/${deviceId}/command`)} disabled={disableActions}>
                            <SendIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                    <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center' }}>
                      <Tooltip title={t('sharedEdit')}>
                        <span>
                          <IconButton onClick={() => navigate(`/settings/device/${deviceId}`)} disabled={disableActions || deviceReadonly}>
                            <EditIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </>
                )}
                {limitCommands && !isAdmin && !isManager && (
                  <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center' }}>
                    <Tooltip title={commandLoading ? 'Enviando comando...' : isBlocked ? t('sharedUnlock') : t('sharedLock')}>
                      <span>
                        <IconButton
                          color={isBlocked ? 'error' : 'success'}
                          onClick={handleLockUnlockClick}
                          disabled={disableActions || deviceReadonly || commandLoading}
                          sx={{
                            backgroundColor: isBlocked ? 'rgba(244, 67, 54, 0.1)' : 'rgba(76, 175, 80, 0.1)',
                            opacity: commandLoading ? 0.6 : 1,
                          }}
                        >
                          {commandLoading ? <CircularProgress size={20} /> : isBlocked ? <LockIcon /> : <LockOpenIcon />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                )}
                {/* ✅ CORREÇÃO 1: Âncora usando hasAnchor */}
                {(isAdmin || isManager || limitCommands) && (
                  <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center' }}>
                    <Tooltip 
                      title={
                        hasAnchor 
                          ? (device.attributes?.lockOnExit 
                              ? 'Âncora Ativa (Bloqueia ao sair)' 
                              : 'Âncora Ativa (Não bloqueia)')
                          : 'Sem Âncora Ativa'
                      }
                    >
                      <span>
                        <IconButton
                          color={
                            hasAnchor 
                              ? (device.attributes?.lockOnExit ? 'error' : 'success')
                              : 'default'
                          }
                          onClick={() => {
                            if (hasAnchor) {
                              setOpenRemoveAnchorDialog(true);
                            } else {
                              setOpenAnchorDialog(true);
                            }
                          }}
                          disabled={disableActions || deviceReadonly}
                          sx={{
                            ...(hasAnchor && {
                              backgroundColor: device.attributes?.lockOnExit 
                                ? 'rgba(244, 67, 54, 0.1)' 
                                : 'rgba(76, 175, 80, 0.1)',
                            }),
                          }}
                        >
                          <AnchorIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                )}
                <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center' }}>
                  <Tooltip title={t('sharedRemove')}>
                    <span>
                      <IconButton color="error" onClick={() => setOpenRemoveDialog(true)} disabled={disableActions || deviceReadonly}>
                        <DeleteIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              </Box>
            </CardActions>
          </Card>
        </Rnd>
      </div>
      {position && (
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
          <MenuItem onClick={() => navigate(`/position/${position.id}`)}>
            <Typography color="primary">{t('sharedShowDetails')}</Typography>
          </MenuItem>
          {!readonly && <MenuItem onClick={handleGeofence}>{t('sharedCreateGeofence')}</MenuItem>}
          <MenuItem component="a" target="_blank" href={`https://www.google.com/maps/search/?api=1&query=${position.latitude}%2C${position.longitude}`} rel="noreferrer">
            {t('linkGoogleMaps')}
          </MenuItem>
          <MenuItem component="a" target="_blank" href={`http://maps.apple.com/?ll=${position.latitude},${position.longitude}`} rel="noreferrer">
            {t('linkAppleMaps')}
          </MenuItem>
          <MenuItem component="a" target="_blank" href={`https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${position.latitude}%2C${position.longitude}&heading=${position.course || 0}`} rel="noreferrer">
            {t('linkStreetView')}
          </MenuItem>
          {navigationAppTitle && (
            <MenuItem component="a" target="_blank" href={navigationAppLink.replace('{latitude}', position.latitude).replace('{longitude}', position.longitude)} rel="noreferrer">
              {navigationAppTitle}
            </MenuItem>
          )}
          {!shareDisabled && !user.temporary && (
            <MenuItem onClick={() => navigate(`/settings/device/${deviceId}/share`)}>
              <Typography color="secondary">{t('deviceShare')}</Typography>
            </MenuItem>
          )}
        </Menu>
      )}

      <RemoveDialog open={openRemoveDialog} endpoint="devices" itemId={deviceId} onResult={handleRemove} />

      <MultiActionDialog open={dialogOpen} actionType={dialogType} itemId={deviceId} onResult={handleDialogResult} />

      <AnchorDialog
        open={openAnchorDialog}
        onClose={() => setOpenAnchorDialog(false)}
        deviceName={device?.name || 'Desconhecido'}
        position={position}
        device={device}
        dispatch={dispatch}
      />

      <RemoveAnchorDialog
        open={openRemoveAnchorDialog}
        onClose={() => setOpenRemoveAnchorDialog(false)}
        deviceName={device?.name || 'Desconhecido'}
        device={device}
        position={position}
        dispatch={dispatch}
      />

      <LockUnlockConfirmationDialog
        open={openLockUnlockDialog}
        onClose={() => setOpenLockUnlockDialog(false)}
        onConfirm={executeLockUnlock}
        isBlocked={isBlocked}
        deviceName={device?.name || 'Desconhecido'}
        loading={commandLoading}
      />

      <SuccessToast
        open={showSuccessToast}
        message={toastMessage}
        severity={toastSeverity}
        onClose={() => {
          setShowSuccessToast(false);
          setToastOpen(false);
        }}
      />
    </>
  );
};

export default StatusCard;
